<template>
  <v-dialog
    :value="value"
    max-width="640"
    scrollable
    @input="$emit('input', $event)"
  >
    <div class="history-popup">
      <div class="history-popup__header">
        <strong class="history-popup__title">정보 변경 이력</strong>
        <p class="history-popup__subtitle">요청 내역을 눌러 상세 정보를 확인하세요.</p>
      </div>

      <div v-if="isLoading" class="history-popup__empty">
        정보를 불러오는 중입니다.
      </div>

      <div v-else-if="normalizedItems.length" class="history-popup__list">
        <button
          v-for="(item, index) in normalizedItems"
          :key="`${item.change_date}-${index}`"
          type="button"
          class="history-card"
          :class="{ 'is-open': isExpanded(index) }"
          @click="toggleItem(index)"
        >
          <div class="history-card__summary">
            <div class="history-card__summary-main">
              <div class="history-card__summary-top">
                <span
                  class="history-card__status"
                  :class="statusClass(item.status)"
                >
                  {{ item.status || "-" }}
                </span>
                <span class="history-card__date">
                  {{ formatDate(item.change_date) }}
                </span>
              </div>
              <strong class="history-card__headline">
                {{ summarizeFields(item.changed_fields) }}
              </strong>
              <p v-if="!isExpanded(index)" class="history-card__summary-sub">
                요청일자: {{ formatDateTime(item.requested_at) }}
              </p>
            </div>
            <div class="history-card__chevron" :class="{ 'is-open': isExpanded(index) }">
              <span></span>
              <span></span>
            </div>
          </div>

          <div v-if="isExpanded(index)" class="history-card__detail">
            <div class="history-card__fields">
              <div
                v-for="(field, fieldIndex) in item.changed_fields"
                :key="`${field.field}-${fieldIndex}`"
                class="history-field"
              >
                <div class="history-field__label">
                  {{ field.label || field.field }}
                </div>
                <div class="history-field__values">
                  <div class="history-field__value is-old">
                    <span>변경 전</span>
                    <strong>{{ field.old || "-" }}</strong>
                  </div>
                  <div class="history-field__arrow">→</div>
                  <div class="history-field__value is-new">
                    <span>변경 후</span>
                    <strong>{{ field.new || "-" }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="getRemarkText(item)" class="history-card__reason">
              <div class="history-card__reason-label">변경사유</div>
              <p class="history-card__reason-text">{{ getRemarkText(item) }}</p>
            </div>
            <div v-if="item.is_rejected" class="history-card__reason">
              <div class="history-card__reason-label">반려사유</div>
              <p class="history-card__reason-text">{{ item.reject_reason }}</p>
            </div>
            <div v-if="getAttachmentText(item)" class="history-card__reason">
              <div class="history-card__reason-label">첨부파일</div>
              <p class="history-card__reason-text" style="text-decoration: underline; cursor: pointer;" @click.stop="downloadAttachment(item)">{{ getAttachmentText(item) }}</p>
            </div>
          </div>
        </button>
      </div>

      <div v-else class="history-popup__empty">
        정보 변경 이력이 없습니다.
      </div>

      <div class="history-popup__footer">
        <button type="button" class="history-popup__footer-button" @click="close">
          닫기
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { getContractorHistoryFile, getContractorInfoChangeHistory } from "@/api/contract";
export default {
  // 계약자 정보 변경 요청 이력을 목록과 상세 형태로 보여주는 다이얼로그
  name: "ContractorInfoChangeHistoryDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: [String, Number],
      default: "",
    },
    isCorporate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedIndexes: [],
      historyItems: [],
      historyLoading: false,
      historyLoaded: false,
    };
  },
  computed: {
    home() {
      return this.$store.getters.getHome;
    },
    isLoading() {
      return this.historyLoading;
    },
    normalizedItems() {
      if (!Array.isArray(this.historyItems)) {
        return [];
      }

      // 응답 형식 차이를 흡수하고 화면용 changed_fields 구조를 정규화한다.
      return this.historyItems.map((item) => ({
        ...item,
        changed_fields: this.normalizeChangedFields(item.changed_fields),
      }));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.expandedIndexes = [];
        if (val) {
          this.fetchHistory();
        }
      },
    },
    contractId() {
      this.resetHistoryState();
      if (this.value) {
        this.fetchHistory();
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    resetHistoryState() {
      this.historyItems = [];
      this.historyLoading = false;
      this.historyLoaded = false;
      this.expandedIndexes = [];
    },
    fetchHistory() {
      if (!this.contractId) {
        this.historyItems = [];
        this.historyLoaded = true;
        this.historyLoading = false;
        return Promise.resolve(true);
      }

      if (this.historyLoading) {
        return Promise.resolve(false);
      }

      if (this.historyLoaded) {
        return Promise.resolve(true);
      }

      this.historyLoading = true;
      return getContractorInfoChangeHistory(this.contractId, this.isCorporate)
        .then((response) => {
          this.historyItems = this.extractHistoryItems(response.data);
          this.historyLoaded = true;
          return true;
        })
        .catch((error) => {
          this.historyItems = [];
          this.historyLoaded = false;
          console.error("Error fetching contractor info change history:", error);
          this.$store.commit(
            "ERROR",
            "정보 변경 이력을 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
          );
          return false;
        })
        .finally(() => {
          this.historyLoading = false;
        });
    },
    extractHistoryItems(data) {
      return Array.isArray(data) ? data : [];
    },
    isExpanded(index) {
      return this.expandedIndexes.includes(index);
    },
    toggleItem(index) {
      if (this.isExpanded(index)) {
        this.expandedIndexes = this.expandedIndexes.filter((item) => item !== index);
        return;
      }

      this.expandedIndexes = [index];
    },
    formatDate(value) {
      const digits = String(value || "").replace(/\D/g, "");

      if (digits.length !== 8) {
        return value || "-";
      }

      return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}`;
    },
    formatDateTime(value) {
      const digits = String(value || "").replace(/\D/g, "");

      if (!digits) {
        return "-";
      }

      if (digits.length !== 14) {
        return value;
      }

      return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)} ${digits.slice(8, 10)}:${digits.slice(10, 12)}`;
    },
    summarizeFields(fields) {
      if (!Array.isArray(fields) || !fields.length) {
        return "변경 항목 없음";
      }

      return fields
        .map((field) => field.label)
        .join(", ");
    },
    getRemarkText(item) {
      return String(item?.remark || "").trim();
    },
    getAttachmentText(item) {
      return String(item?.file_name || "").trim();
    },
    downloadAttachment(item) {
      console.log(item);
      const no_seq = item?.no_seq;
      const homd_id = this.home.id;


      return getContractorHistoryFile(homd_id, no_seq)
        .then((response) => {
          const blob = response.data;
          const fileName = item?.file_name || "";
          const fileType = blob?.type || "application/octet-stream";
          // 다운로드
          const file = new File([blob], fileName, {
            type: fileType || "application/octet-stream",
          });
          window.open(URL.createObjectURL(file), "_blank");
        })
        .catch((error) => {
          console.error("Error fetching contractor history file:", error);
          this.$store.commit(
            "ERROR",
            "첨부파일을 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
          );
          return null;
        })      
    },
    formatChangedFieldValue(fieldName, value) {
      if (fieldName === "citizen_number") {
        return this.formatMaskedCitizenNumber(value);
      }

      return value;
    },
    formatMaskedCitizenNumber(value) {
      const digits = String(value || "").replace(/\D/g, "");

      if (digits.length < 7) {
        return value || "-";
      }

      return `${digits.slice(0, 6)}-${digits[6]}${"*".repeat(6)}`;
    },
    normalizeChangedFields(fields) {
      if (!Array.isArray(fields) || !fields.length) {
        return [];
      }

      // 주소/우편번호처럼 분리된 서버 필드는 화면에서 한 묶음으로 합쳐 보여준다.
      const mappedFields = fields.map((field) => ({
        ...field,
        label: field.label,
        old: this.formatChangedFieldValue(field.field, field.old),
        new: this.formatChangedFieldValue(field.field, field.new),
      }));
      const addressField = mappedFields.find((field) => field.field === "address1");
      const postCodeField = mappedFields.find((field) => field.field === "post_code");
      const normalizedFields = mappedFields.filter(
        (field) => field.field !== "address1" && field.field !== "post_code"
      );

      if (addressField || postCodeField) {
        normalizedFields.push({
          field: "address",
          label: "주소",
          old: this.combineAddressValue(
            addressField ? addressField.old : "",
            postCodeField ? postCodeField.old : ""
          ),
          new: this.combineAddressValue(
            addressField ? addressField.new : "",
            postCodeField ? postCodeField.new : ""
          ),
        });
      }

      return normalizedFields;
    },
    combineAddressValue(address, postCode) {
      const normalizedAddress = String(address || "").trim();
      const normalizedPostCode = String(postCode || "").trim();

      if (normalizedAddress && normalizedPostCode) {
        return `(${normalizedPostCode}) ${normalizedAddress}`;
      }

      return normalizedAddress || normalizedPostCode || "-";
    },
    statusClass(status) {
      if (status === "완료") {
        return "is-complete";
      }

      if (status === "처리중" || status === "진행중") {
        return "is-progress";
      }

      if(status === "반려" || status === "취소") {
        return "is_cancel";
      }

      return "";
    },
  },
};
</script>

<style scoped>
.history-popup {
  border-radius: 20px;
  background-color: #ffffff;
  padding: 26px 24px 0;
  overflow: hidden;
  text-align: left;
}

.history-popup__header {
  margin-bottom: 20px;
  text-align: center;
}

.history-popup__title {
  display: block;
  color: #e66a1f;
  font-size: 24px;
  font-weight: 700;
}

.history-popup__subtitle {
  margin: 8px 0 0;
  color: #b8b8b8;
  font-size: 13px;
  font-weight: 700;
}

.history-popup__list {
  max-height: calc(72vh - 72px);
  overflow-y: auto;
  padding: 0 0 16px;
  -webkit-overflow-scrolling: touch;
}

.history-card {
  display: block;
  width: 100%;
  border: 1px solid #252525;
  border-radius: 20px;
  background-color: #ffffff;
  padding: 0;
  text-align: left;
}

.history-card + .history-card {
  margin-top: 10px;
}

.history-card__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 13px;
}

.history-card__summary-main {
  min-width: 0;
  flex: 1 1 auto;
}

.history-card__summary-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.history-card__date {
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 700;
}

.history-card__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  height: 28px;
  border-radius: 999px;
  padding: 0 12px;
  background-color: #d9d9d9;
  color: #232323;
  font-size: 12px;
  font-weight: 700;
}

.history-card__status.is-complete {
  background-color: #e66a1f;
  color: #ffffff;
}

.history-card__status.is-progress {
  background-color: #00181a;
  color: #ffffff;
}

.history-card__status.is_cancel {
  color: #FF0000;
}

.history-card__headline {
  display: block;
  margin-bottom: 5px;
  color: #232323;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.history-card__summary-sub {
  display: flex;
  align-items: center;
  margin: 0;
  color: #8c8c8c;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  flex-wrap: wrap;
}

.history-card__dot {
  width: 3px;
  height: 3px;
  margin: 0 6px;
  border-radius: 50%;
  background-color: #b8b8b8;
}

.history-card__chevron {
  position: relative;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
}

.history-card__chevron span {
  position: absolute;
  top: 11px;
  width: 9px;
  height: 2px;
  border-radius: 999px;
  background-color: #ef761d;
  transition: transform 0.2s ease;
}

.history-card__chevron span:first-child {
  left: 4px;
  transform: rotate(45deg);
}

.history-card__chevron span:last-child {
  right: 4px;
  transform: rotate(-45deg);
}

.history-card__chevron.is-open span:first-child {
  transform: rotate(-45deg);
}

.history-card__chevron.is-open span:last-child {
  transform: rotate(45deg);
}

.history-card__detail {
  padding: 8px 14px 14px;
  border-top: 1px solid #e5e5e5;
}

.history-field + .history-field {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e5e5;
}

.history-field__label {
  margin-bottom: 8px;
  color: #b8b8b8;
  font-size: 13px;
  font-weight: 700;
}

.history-field__values {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-field__value {
  flex: 1 1 0;
  border: 1px solid #252525;
  border-radius: 0;
  padding: 10px 11px;
  background-color: #ffffff;
  border-radius: 6px;
}

.history-field__value span {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
}

.history-field__value strong {
  color: #232323;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  word-break: break-all;
}

.history-field__value.is-old {
  background-color: #f8f8f8;
}

.history-field__value.is-old span {
  color: #8c8c8c;
}

.history-field__value.is-new {
  background-color: #fff7f1;
}

.history-field__value.is-new span {
  color: #ef761d;
}

.history-field__arrow {
  flex: 0 0 auto;
  color: #ef761d;
  font-size: 18px;
  font-weight: 700;
}

.history-card__reason {
  margin-top: 14px;
  border-top: 1px solid #e5e5e5;
  padding-top: 14px;
}

.history-card__reason-label {
  margin-bottom: 8px;
  color: #b8b8b8;
  font-size: 13px;
  font-weight: 700;
}

.history-card__reason-text {
  margin: 0;
  color: #232323;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
  word-break: keep-all;
}

.history-popup__empty {
  border: 1px solid #252525;
  border-radius: 0;
  background-color: #ffffff;
  padding: 40px 20px;
  text-align: center;
  color: #7d7d7d;
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 16px;
  border-radius: 6px;
}

.history-popup__footer {
  display: flex;
  justify-content: center;
  padding: 16px 0 22px;
  border-top: 1px solid #e5e5e5;
  background-color: #ffffff;
}

.history-popup__footer-button {
  flex: 1 1 auto;
  height: 40px;
  border: 0;
  border-radius: 999px;
  background-color: #d9d9d9;
  color: #232323;
  font-size: 14px;
  font-weight: 700;
}

@media (min-width: 601px) {
  .history-popup {
    border-radius: 24px;
    padding: 26px 24px 0;
  }

  .history-field__values {
    align-items: center;
    flex-direction: row;
    gap: 10px;
  }

  .history-popup__footer {
    padding-right: 0;
    padding-left: 0;
  }

  .history-popup__footer-button {
    max-width: 180px;
  }
}

@media (max-width: 600px) {
  .history-field__values {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .history-field__arrow {
    text-align: center;
    transform: rotate(90deg);
  }
}
</style>
